/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 */

/*
 * urls.js — the ONE resolver for every URL embedded in an outgoing email.
 *
 * WHY THIS EXISTS: an email is read on someone else's machine, long after it left
 * ours. A URL that resolves against the ENV host (`config.my.app.url`, e.g.
 * https://egt.studio.test in development) is a broken image / dead link in the
 * recipient's inbox — exactly the bug the founder found in a real delivered email
 * (a giant, broken EGT logo pointing at the .test dev host).
 *
 * CONTRACT: email elements MUST build absolute URLs from emailBaseUrl(config), NEVER
 * from config.my.app.url.
 *
 * Resolution order (first PUBLIC absolute http(s) origin wins):
 *   1. config.email.publicBaseUrl      — the intended, explicit answer.
 *   2. config.email.tracking.base      — the public origin the tracking links use.
 *   3. config.my.app.url               — ONLY when it is a public host (so a normal
 *                                        production tenant keeps working), never a
 *                                        .test / .local / localhost / bare-IP host.
 *   4. ""                              — FAIL CLOSED. Callers must degrade (omit the
 *                                        image / link) rather than emit a dev URL.
 */

/**
 * Extract the lowercase hostname from an absolute URL (no URL parser: this runs in
 * the server render path and must never throw on junk input).
 * @param {string} u
 * @returns {string} hostname, or "" when the value is not an absolute URL.
 */
function hostOf(u) {
    var m = /^[a-z][a-z0-9+.-]*:\/\/([^\/?#]+)/i.exec(String(u == null ? "" : u).trim());
    if (!m) { return ""; }
    var authority = m[1];
    var at = authority.lastIndexOf("@");
    if (at >= 0) { authority = authority.slice(at + 1); }
    return authority.split(":")[0].toLowerCase();
}

/**
 * Is this a host a stranger's email client could actually reach?
 * Rejects dev/reserved TLDs, localhost, bare IPs and single-label hosts.
 * @param {string} host
 * @returns {boolean}
 */
function isPublicHost(host) {
    if (!host) { return false; }
    if (host === "localhost") { return false; }
    if (/\.(test|local|localhost|internal|invalid|example|localdomain)$/.test(host)) { return false; }
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) { return false; } // bare IPv4
    if (host.indexOf(":") >= 0) { return false; }               // IPv6 literal
    if (host.indexOf(".") < 0) { return false; }                // single-label intranet name
    return true;
}

/**
 * True when the value is an absolute http(s) URL on a publicly reachable host.
 * @param {*} u
 * @returns {boolean}
 */
function isPublicUrl(u) {
    var s = String(u == null ? "" : u).trim();
    if (!/^https?:\/\//i.test(s)) { return false; }
    return isPublicHost(hostOf(s));
}

/**
 * The public origin every emailed URL must be built from. See the file header.
 * @param {object} config The scafolding config ($.config).
 * @returns {string} An absolute origin with NO trailing slash, or "" (fail closed).
 */
function emailBaseUrl(config) {
    var cfg = config || {};
    var email = cfg.email || {};
    var candidates = [
        email.publicBaseUrl,
        (email.tracking && email.tracking.base),
        (cfg.my && cfg.my.app && cfg.my.app.url)
    ];
    for (var i = 0; i < candidates.length; i++) {
        var c = (typeof candidates[i] === "string") ? candidates[i].trim() : "";
        if (c && isPublicUrl(c)) { return c.replace(/\/+$/, ""); }
    }
    return "";
}

/**
 * Join a path onto the public email origin. Absolute inputs pass through untouched.
 * @param {object} config
 * @param {string} path e.g. "/logo.png"
 * @returns {string} An absolute URL, or "" when no public origin is configured.
 */
function emailUrl(config, path) {
    var p = (typeof path === "string") ? path.trim() : "";
    if (/^(https?:)?\/\//i.test(p) || /^data:/i.test(p)) { return p; }
    var base = emailBaseUrl(config);
    if (!base) { return ""; }
    if (!p) { return base; }
    return base + (p.charAt(0) === "/" ? "" : "/") + p;
}

module.exports = {
    emailBaseUrl: emailBaseUrl,
    emailUrl: emailUrl,
    isPublicUrl: isPublicUrl,
    isPublicHost: isPublicHost,
    hostOf: hostOf
};
